// The module's interface against the manifest (spec §4, §5): required exports and their types, host imports,
// memory. Shared by tools/rtw-check and the host, which refuses a build that fails it before anything runs.
import { EXPORT_ARITY, EXPORTS_OPTIONAL, HOST_IMPORTS, IMPORT_ARITY, IMPORTS_NET, netModel, requiredExports, type Manifest } from './manifest.ts';
import { inspectWasm, type FuncType } from './wasm-inspect.ts';

export interface ModuleCheck { errors: string[]; warnings: string[] }

function sigOk(t: FuncType | undefined, params: number, ptrType: 'i32' | 'i64'): boolean {
  if (!t) return false;
  const okType = (v: string) => v === 'i32' || v === ptrType;
  return t.params.length === params && t.params.every(okType) && t.results.length === 1 && okType(t.results[0]);
}

export function checkModule(m: Manifest, wasm: Uint8Array): ModuleCheck {
  const errors: string[] = [];
  const warnings: string[] = [];
  const err = (s: string) => errors.push(s);
  const warn = (s: string) => warnings.push(s);
  let info;
  try { info = inspectWasm(wasm); } catch (e) { err(`${m.files.wasm.path}: ${(e as Error).message}`); return { errors, warnings }; }
  const ptr: 'i32' | 'i64' = m.memory?.memory64 ? 'i64' : 'i32';
  const hasGlue = !!m.files.js;
  const exports = new Map(info.exports.map((x) => [x.name, x]));
  for (const name of requiredExports(m)) {
    const x = exports.get(name) ?? exports.get('_' + name);
    if (!x) {
      (hasGlue ? warn : err)(`missing export ${name}${hasGlue ? ' (may be renamed by the JS glue; checked again at run time)' : ''}`);
    } else if (x.kind !== 'func') err(`export ${name} is a ${x.kind}, not a function`);
    else if (!sigOk(x.type, EXPORT_ARITY[name], ptr)) err(`export ${name} must take ${EXPORT_ARITY[name]} ${ptr} and return one ${ptr}`);
  }
  for (const name of EXPORTS_OPTIONAL) {
    const x = exports.get(name) ?? exports.get('_' + name);
    if (x && (x.kind !== 'func' || !sigOk(x.type, EXPORT_ARITY[name], ptr))) err(`export ${name} must take ${EXPORT_ARITY[name]} ${ptr} and return one ${ptr}`);
  }
  for (const x of info.exports) {
    if (x.name === 'rtw_net_send' || x.name === '_rtw_net_send') warn(`export ${x.name} is the 1.0 draft: since 1.2 rtw_net_send is a host import (spec §4.12)`);
    else if (/^_?rtw_/.test(x.name) && !(x.name.replace(/^_/, '') in EXPORT_ARITY)) warn(`unknown export ${x.name} (not in v1)`);
  }
  const known = new Set<string>(HOST_IMPORTS);
  for (const imp of info.imports) {
    if (imp.module === 'env' && imp.name.startsWith('rtw_')) {
      if (!known.has(imp.name)) err(`imports unknown host function env.${imp.name}`);
      else if ((IMPORTS_NET as readonly string[]).includes(imp.name) && !netModel(m)) err(`imports env.${imp.name}, which needs "net": { "players": 2..4 } in the manifest`);
      else if (imp.kind !== 'func' || !sigOk(imp.type, IMPORT_ARITY[imp.name], ptr)) {
        err(`import env.${imp.name} must take ${IMPORT_ARITY[imp.name]} ${ptr} and return one ${ptr}`);
      }
    } else if (imp.module === 'env' && imp.name === 'memory' && imp.kind === 'memory') {
      if (!!imp.memory64 !== !!m.memory?.memory64) err('env.memory: memory64 does not match manifest memory.memory64');
    } else if (!hasGlue) {
      err(`imports ${imp.module}.${imp.name}; without files.js only env.rtw_* and env.memory can be provided`);
    }
  }
  if (!hasGlue) {
    const mem = exports.get('memory');
    const importsMem = info.imports.some((i) => i.module === 'env' && i.name === 'memory');
    if (!importsMem && (!mem || mem.kind !== 'memory')) err('must export its memory as "memory" (or import env.memory)');
  }
  // With glue, at least the module must look like a retrowser build: some rtw_ exports must exist.
  if (hasGlue && !info.exports.some((x) => /^_?rtw_/.test(x.name))) err('the module exports no rtw_* functions, so it is not a retrowser build');
  return { errors, warnings };
}
